import { useState } from 'react';

const FINAL_LESSONS = [
  { emoji: '🍳', text: 'Chọn đúng dụng cụ nấu ăn' },
  { emoji: '🙌', text: 'Rửa tay đủ 6 bước với xà phòng' },
  { emoji: '🎋', text: 'Rửa sạch dụng cụ trước khi dùng' },
  { emoji: '🌾', text: 'Vo gạo cho đến khi nước trong' },
  { emoji: '🎍', text: 'Chuẩn bị ống nứa đúng cách (nhồi 2/3)' },
  { emoji: '🔥', text: 'Nhóm lửa phải có người lớn giúp' },
  { emoji: '🌡️', text: 'Giữ lửa vừa đủ khi nướng' },
  { emoji: '🔄', text: 'Xoay ống đều để cơm chín đều' },
  { emoji: '✅', text: 'Ăn chín uống sôi để không bị đau bụng' },
];

interface SummaryScreenProps {
  score: number;
  cleanBar: number;
  bacteriaBar: number;
  cookedBar: number;
  roundsCompleted: number;
  testScore: number;
  onBack: () => void;
  onRestart: () => void;
}

function saveAchievement(data: object) {
  try {
    const key = 'bahnar_achievements';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.unshift({ ...data, date: new Date().toLocaleDateString('vi-VN') });
    localStorage.setItem(key, JSON.stringify(existing.slice(0, 50)));
  } catch (_) { /* ignore */ }
}

export default function SummaryScreen({ score, cleanBar, bacteriaBar, cookedBar, roundsCompleted, testScore, onBack, onRestart }: SummaryScreenProps) {
  const [saved, setSaved] = useState(false);
  const [showCelebration, setShowCelebration] = useState(true);

  const stars = score >= 900 ? 3 : score >= 600 ? 2 : 1;
  const medal = score >= 900 ? '🥇' : score >= 600 ? '🥈' : '🥉';
  const message = score >= 900 ? 'Xuất sắc! Bạn là đầu bếp an toàn thực phẩm thực thụ!' : score >= 600 ? 'Rất giỏi! Bạn đã hiểu cách nấu cơm lam an toàn!' : 'Tốt lắm! Hãy luyện tập thêm để thành thục hơn nhé!';

  const handleSave = () => {
    saveAchievement({
      game: 'Thi nướng cơm lam',
      score,
      stars,
      roundsCompleted,
      cleanBar: Math.round(cleanBar),
      cookedBar: Math.round(cookedBar),
    });
    setSaved(true);
  };

  setTimeout(() => setShowCelebration(false), 3000);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-primary-cream flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Confetti effect */}
      {showCelebration && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute text-2xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                animation: `fall${i % 3} ${1.5 + Math.random() * 2}s linear ${Math.random() * 1}s forwards`,
                fontSize: `${16 + Math.random() * 16}px`,
              }}
            >
              {['🎉', '⭐', '✨', '🌟', '🎊', '🥳'][i % 6]}
            </div>
          ))}
        </div>
      )}

      <div className="max-w-lg w-full text-center">
        {/* Medal */}
        <div className="text-8xl mb-4 animate-bounce">{medal}</div>
        <div className="flex justify-center gap-1 mb-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i} className={`text-3xl transition-all ${i < stars ? 'opacity-100' : 'opacity-20'}`}>⭐</span>
          ))}
        </div>
        <h2 className="text-2xl font-extrabold text-primary-brown-dark mb-2">{message}</h2>

        {/* Score */}
        <div className="bg-white rounded-3xl p-5 mb-5">
          <div className="text-5xl font-extrabold text-primary-orange mb-1">{score}</div>
          <div className="text-sm text-primary-brown-light">điểm số</div>
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="text-center">
              <div className="text-xs text-primary-brown-light mb-1">Sạch sẽ</div>
              <div className="text-lg font-bold text-sky-600">{Math.round(cleanBar)}%</div>
            </div>
            <div className="text-center border-l border-gray-100">
              <div className="text-xs text-primary-brown-light mb-1">Chín</div>
              <div className="text-lg font-bold text-amber-600">{Math.round(cookedBar)}%</div>
            </div>
            <div className="text-center border-l border-gray-100">
              <div className="text-xs text-primary-brown-light mb-1">Vi khuẩn</div>
              <div className="text-lg font-bold text-red-600">{Math.round(bacteriaBar)}%</div>
            </div>
            <div className="text-center border-l border-gray-100">
              <div className="text-xs text-primary-brown-light mb-1">Bài kiểm tra</div>
              <div className="text-lg font-bold text-green-600">{testScore}đ</div>
            </div>
          </div>
        </div>

        {/* Lessons learned */}
        <div className="bg-white rounded-3xl p-5 mb-6 text-left">
          <h3 className="font-bold text-primary-brown-dark text-center mb-4">📚 Những điều em đã học</h3>
          <div className="space-y-2">
            {FINAL_LESSONS.map((l, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-amber-50 rounded-xl">
                <span className="text-lg flex-shrink-0">{l.emoji}</span>
                <p className="text-sm font-semibold text-primary-brown">{l.text}</p>
                <div className="ml-auto w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-circle-fill text-green-500"></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!saved ? (
            <button onClick={handleSave} className="flex-1 py-3.5 bg-gradient-to-r from-green-500 to-emerald-400 text-white font-bold rounded-2xl cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-trophy-fill"></i>
              </div>
              Lưu thành tích
            </button>
          ) : (
            <div className="flex-1 py-3.5 bg-green-100 text-green-700 font-bold rounded-2xl flex items-center justify-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-fill"></i>
              </div>
              Đã lưu!
            </div>
          )}
          <button onClick={onRestart} className="flex-1 py-3.5 border-2 border-primary-orange text-primary-orange font-bold rounded-2xl cursor-pointer whitespace-nowrap hover:bg-primary-orange/5">
            <div className="w-5 h-5 inline-flex items-center justify-center mr-1">
              <i className="ri-restart-line"></i>
            </div>
            Chơi lại
          </button>
          <button onClick={onBack} className="flex-1 py-3.5 bg-primary-brown-dark text-white font-bold rounded-2xl cursor-pointer whitespace-nowrap hover:opacity-90 flex items-center justify-center gap-1">
            <div className="w-5 h-5 inline-flex items-center justify-center">
              <i className="ri-home-4-line"></i>
            </div>
            Trở về Trang chủ trò chơi
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fall0 { to { transform: translateY(110vh) rotate(360deg); } }
        @keyframes fall1 { to { transform: translateY(110vh) rotate(-360deg) translateX(50px); } }
        @keyframes fall2 { to { transform: translateY(110vh) rotate(180deg) translateX(-50px); } }
      `}</style>
    </div>
  );
}
